import { LoadingState } from '@/components/primitives/LoadingState'
import { PageHeader } from '@/components/primitives/PageHeader'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { API_BASE_URL, apiFetch } from '@/lib/api'
import { featurePost, hidePost, movePostToFeed } from '@/lib/p0'
import { type Flag, fetchFlags, patchFlag } from '@/lib/p6'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Activity,
  ArrowLeftRight,
  BarChart3,
  Blocks,
  Eye,
  FileText,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Pin,
  RefreshCw,
  Repeat2,
  Settings2,
  ShieldCheck
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

type FeedConfig = {
  title: string
  subtitle: string
  description: string
  icon: typeof Activity
  color: string
  master: string
  features: Array<{ key: string; label: string; description: string }>
}

const FEEDS: FeedConfig[] = [
  {
    title: 'Live Feed',
    subtitle: 'The main social feed',
    description:
      'Control posts, conversations, discovery, and the modules people see while browsing Live.',
    icon: Activity,
    color: 'text-sky-600',
    master: 'enable_social',
    features: [
      {
        key: 'enable_posts',
        label: 'Posts',
        description: 'Allow people to create and see feed posts.'
      },
      {
        key: 'enable_stories',
        label: 'Stories',
        description: 'Show temporary stories in the feed.'
      },
      {
        key: 'enable_following_feed',
        label: 'Following feed',
        description: 'Allow the Following view.'
      },
      {
        key: 'enable_explore_section',
        label: 'Explore section',
        description: 'Show discovery categories above the feed.'
      },
      {
        key: 'enable_feed_trending_bar',
        label: 'Trending bar',
        description:
          'Trending bar at the top of Live, Blocks, and Stocks feeds.'
      },
      {
        key: 'enable_suggested_users',
        label: 'Suggested people',
        description: 'Recommend people to follow.'
      },
      {
        key: 'enable_feed_bites',
        label: 'Bites module',
        description: 'Show restaurant and food suggestions.'
      },
      {
        key: 'enable_feed_events',
        label: 'Events module',
        description: 'Show upcoming events in Live.'
      },
      {
        key: 'enable_feed_services',
        label: 'Services module',
        description: 'Show local service recommendations.'
      },
      {
        key: 'enable_feed_shops',
        label: 'Shops module',
        description: 'Show shop recommendations.'
      },
      {
        key: 'enable_comments',
        label: 'Comments',
        description: 'Allow comments on posts.'
      },
      {
        key: 'enable_post_likes',
        label: 'Likes',
        description: 'Allow people to like posts.'
      },
      {
        key: 'enable_reposting',
        label: 'Reposts',
        description: 'Allow people to repost content.'
      },
      {
        key: 'enable_quote_post',
        label: 'Quote posts',
        description: 'Allow people to add commentary when reposting.'
      },
      {
        key: 'enable_sharing',
        label: 'Sharing',
        description: 'Allow posts to be shared.'
      }
    ]
  },
  {
    title: 'Blocks Feed',
    subtitle: 'Community conversations',
    description:
      'Control community spaces, discussions, and polls without affecting Live or Stocks.',
    icon: Blocks,
    color: 'text-violet-600',
    master: 'enable_communities',
    features: [
      {
        key: 'enable_feed_trending_bar',
        label: 'Trending bar',
        description:
          'Trending bar at the top of Live, Blocks, and Stocks feeds.'
      },
      {
        key: 'enable_community_polls',
        label: 'Community polls',
        description: 'Allow polls inside Blocks.'
      },
      {
        key: 'enable_comments',
        label: 'Comments',
        description: 'Allow discussion on community posts.'
      },
      {
        key: 'enable_post_likes',
        label: 'Likes',
        description: 'Allow reactions to community posts.'
      },
      {
        key: 'enable_reposting',
        label: 'Reposts',
        description: 'Allow community posts to be reposted.'
      },
      {
        key: 'enable_report_post',
        label: 'Reports',
        description: 'Allow people to report harmful content.'
      }
    ]
  },
  {
    title: 'Stocks Feed',
    subtitle: 'Market conversations',
    description:
      'Control stock discussions, watchlists, alerts, and trading modes separately from the social feeds.',
    icon: BarChart3,
    color: 'text-emerald-600',
    master: 'enable_stocks',
    features: [
      {
        key: 'enable_feed_trending_bar',
        label: 'Trending bar',
        description:
          'Trending bar at the top of Live, Blocks, and Stocks feeds.'
      },
      {
        key: 'enable_stock_watchlist',
        label: 'Watchlists',
        description: 'Allow people to track stocks.'
      },
      {
        key: 'enable_stock_price_alerts',
        label: 'Price alerts',
        description: 'Allow price alert rules.'
      },
      {
        key: 'enable_stock_watchlist_notifications',
        label: 'Watchlist notifications',
        description: 'Send updates for watched stocks.'
      },
      {
        key: 'enable_stock_alert_emails',
        label: 'Alert emails',
        description: 'Send stock alerts by email.'
      },
      {
        key: 'enable_paper_trading',
        label: 'Paper trading',
        description: 'Allow simulated trading.'
      },
      {
        key: 'enable_live_trading',
        label: 'Live trading',
        description: 'Allow real trading actions.'
      }
    ]
  }
]

type FeedMedia = { url?: string; media_type?: string; alt_text?: string }
type FeedPost = {
  id: number
  caption?: string
  media?: FeedMedia[]
  author?: { username?: string; display_name?: string }
  created_at?: string
  is_pinned?: boolean
  likes_count?: number
  comments_count?: number
  reposts_count?: number
  community_id?: number | null
  stock_symbol?: string | null
}

type PostsResponse = {
  results: FeedPost[]
  count?: number
  next?: string | null
  previous?: string | null
}
type Option = {
  id: number
  name?: string
  title?: string
  slug?: string
  symbol?: string
}
type OptionsResponse = { results?: Option[] } | Option[]
type FeedTab = 'live' | 'blocks' | 'stocks'

export function FeedControlPage() {
  const [selectedFeed, setSelectedFeed] = useState<FeedTab>('live')
  const [page, setPage] = useState(1)
  const flags = useQuery({
    queryKey: ['super-flags'],
    queryFn: fetchFlags,
    staleTime: 30_000
  })
  const posts = useQuery({
    queryKey: ['feed-visual-posts', selectedFeed, page],
    queryFn: () =>
      apiFetch<PostsResponse>(
        selectedFeed === 'blocks'
          ? `/api/posts/?page=${page}&blocks_only=true`
          : selectedFeed === 'stocks'
            ? `/api/posts/?page=${page}&stock_feed=true`
            : `/api/posts/?page=${page}&feed_type=live`
      ),
    staleTime: 30_000
  })
  useEffect(() => setPage(1), [selectedFeed])
  const communities = useQuery({
    queryKey: ['feed-share-communities'],
    queryFn: () => apiFetch<OptionsResponse>('/api/communities/?page_size=100'),
    staleTime: 60_000
  })
  const categories = useQuery({
    queryKey: ['feed-share-categories'],
    queryFn: () =>
      apiFetch<OptionsResponse>('/api/post-categories/?page_size=100'),
    staleTime: 60_000
  })
  const symbols = useQuery({
    queryKey: ['feed-share-symbols'],
    queryFn: () => apiFetch<Option[]>('/api/stocks/trending/'),
    staleTime: 60_000
  })
  const [pending, setPending] = useState<{ flag: Flag; value: boolean } | null>(
    null
  )
  const [pendingPost, setPendingPost] = useState<{
    post: FeedPost
    action: 'hide' | 'pin' | 'unpin'
  } | null>(null)
  const [sharePost, setSharePost] = useState<FeedPost | null>(null)
  const [superPowersOpen, setSuperPowersOpen] = useState(false)
  const [shareTarget, setShareTarget] = useState<'live' | 'blocks' | 'stocks'>(
    'live'
  )
  const [communityId, setCommunityId] = useState('')
  const [stockSymbol, setStockSymbol] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [search, setSearch] = useState('')
  const [mediaFilter, setMediaFilter] = useState<
    'all' | 'text' | 'image' | 'video'
  >('all')
  const flagMap = useMemo(
    () => new Map((flags.data?.flags ?? []).map((flag) => [flag.key, flag])),
    [flags.data]
  )
  const activeFeed =
    FEEDS.find(
      (feed) =>
        feed.master ===
        `enable_${selectedFeed === 'live' ? 'social' : selectedFeed === 'blocks' ? 'communities' : 'stocks'}`
    ) ?? FEEDS[0]
  const visiblePosts = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (posts.data?.results ?? [])
      .filter((post) => {
        const mediaType = post.media?.[0]?.media_type?.toLowerCase() ?? ''
        const kind = mediaType.includes('video')
          ? 'video'
          : mediaType
            ? 'image'
            : 'text'
        return (
          (!term ||
            `${post.caption ?? ''} ${post.author?.username ?? ''} ${post.author?.display_name ?? ''}`
              .toLowerCase()
              .includes(term)) &&
          (mediaFilter === 'all' || kind === mediaFilter)
        )
      })
      .sort(
        (a, b) => Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned))
      )
  }, [posts.data, search, mediaFilter])

  const update = useMutation({
    mutationFn: ({ flag, value }: { flag: Flag; value: boolean }) =>
      patchFlag(flag.key, value),
    onSuccess: (_data, variables) => {
      toast.success(
        `${variables.flag.label}: ${variables.value ? 'On' : 'Off'}`
      )
      queryClient.invalidateQueries({ queryKey: ['super-flags'] })
      setPending(null)
    },
    onError: (error) => notifyError('Could not update feed control', error)
  })

  const postAction = useMutation({
    mutationFn: ({
      post,
      action
    }: { post: FeedPost; action: 'hide' | 'pin' | 'unpin' }) =>
      action === 'hide'
        ? hidePost(post.id, 'Hidden from Feed Control Center')
        : featurePost(post.id, action === 'pin'),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.action === 'hide'
          ? 'Post hidden'
          : variables.action === 'pin'
            ? 'Post pinned'
            : 'Post unpinned'
      )
      const pinned = variables.action === 'pin'
      queryClient.setQueriesData<PostsResponse>(
        { queryKey: ['feed-visual-posts'] },
        (current) =>
          current
            ? {
                ...current,
                results: current.results.map((post) =>
                  post.id === variables.post.id
                    ? { ...post, is_pinned: pinned }
                    : post
                )
              }
            : current
      )
      queryClient.invalidateQueries({ queryKey: ['feed-visual-posts'] })
      setPendingPost(null)
    },
    onError: (error) => notifyError('Could not update post', error)
  })

  const moveAction = useMutation({
    mutationFn: () => {
      if (!sharePost) throw new Error('Select a post first.')
      return movePostToFeed(sharePost.id, {
        target: shareTarget,
        ...(shareTarget === 'blocks'
          ? { community_id: Number(communityId) }
          : {}),
        ...(shareTarget === 'stocks' ? { stock_symbol: stockSymbol } : {}),
        ...(categoryId ? { category_id: Number(categoryId) } : {})
      })
    },
    onSuccess: () => {
      toast.success('Post shared to the selected feed')
      queryClient.invalidateQueries({ queryKey: ['feed-visual-posts'] })
      setSharePost(null)
    },
    onError: (error) => notifyError('Could not share post', error)
  })

  function change(flag: Flag, value: boolean) {
    if (
      !value &&
      flag.key.startsWith('enable_') &&
      (flag.key === 'enable_social' ||
        flag.key === 'enable_communities' ||
        flag.key === 'enable_stocks')
    ) {
      setPending({ flag, value })
      return
    }
    update.mutate({ flag, value })
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Feed Control Center"
        description="Turn feed areas and their features on or off using simple controls. Changes apply to the platform immediately."
        meta={
          <Badge variant="secondary">
            {flags.data?.total ?? 0} platform controls available
          </Badge>
        }
      />

      <Card className="rounded-3xl border-primary/20 bg-primary/[0.03] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              {selectedFeed} feed
            </p>
            <h2 className="mt-1 text-xl font-semibold">
              Super Powers for{' '}
              {selectedFeed === 'live'
                ? 'Live'
                : selectedFeed === 'blocks'
                  ? 'Blocks'
                  : 'Stocks'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage this feed and its sub-features from one simple control
              panel.
            </p>
          </div>
          <Button
            onClick={() => setSuperPowersOpen(true)}
            className="rounded-full"
          >
            <Settings2 className="size-4" /> Open Super Powers
          </Button>
        </div>
      </Card>

      <section className="space-y-3 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Feed content</h2>
            <p className="text-sm text-muted-foreground">
              See what users are actually publishing, then moderate it visually.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/apps/feed/posts">Open detailed moderation</Link>
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 rounded-2xl border bg-background p-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search posts or authors…"
            className="min-w-[220px] flex-1"
          />
          <select
            value={mediaFilter}
            onChange={(event) =>
              setMediaFilter(event.target.value as typeof mediaFilter)
            }
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            <option value="all">All posts</option>
            <option value="text">Text</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => posts.refetch()}
            disabled={posts.isFetching}
          >
            <RefreshCw
              className={cn('size-3.5', posts.isFetching && 'animate-spin')}
            />{' '}
            Refresh
          </Button>
        </div>
        {posts.isLoading ? (
          <LoadingState rows={5} />
        ) : posts.isError ? (
          <Card className="rounded-2xl p-6 text-sm text-muted-foreground">
            Feed content could not be loaded.
          </Card>
        ) : (
          <div className="mx-auto w-full max-w-6xl columns-1 gap-5 md:columns-2">
            {visiblePosts.map((post) => (
              <FeedContentCard
                key={post.id}
                post={post}
                onAction={(action) => setPendingPost({ post, action })}
                onShare={() => setSharePost(post)}
              />
            ))}
            {visiblePosts.length === 0 && (
              <Card className="rounded-2xl p-6 text-sm text-muted-foreground">
                No recent posts to review.
              </Card>
            )}
          </div>
        )}
        {posts.data && (posts.data.previous || posts.data.next) && (
          <div className="flex items-center justify-between rounded-2xl border bg-background p-3">
            <p className="text-sm text-muted-foreground">
              {posts.data.count ?? 0} posts · Page {page}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!posts.data.previous || posts.isFetching}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!posts.data.next || posts.isFetching}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </section>

      <Card className="rounded-2xl border-dashed p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 size-5 text-primary" />
          <div>
            <p className="font-semibold">What these controls do</p>
            <p className="mt-1 text-sm text-muted-foreground">
              These are the same live feature switches used by Super Powers.
              They control visibility and access on the public platform; they do
              not delete posts, communities, or stock data.
            </p>
          </div>
        </div>
      </Card>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off {pending?.flag.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately disable this feed for users. Existing data
              will stay safe and can be shown again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={update.isPending}>
              Keep on
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={update.isPending}
              onClick={() => pending && update.mutate(pending)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Turn off feed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingPost !== null}
        onOpenChange={(open) =>
          !open && !postAction.isPending && setPendingPost(null)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingPost?.action === 'hide'
                ? 'Hide this post?'
                : pendingPost?.action === 'pin'
                  ? 'Pin this post?'
                  : 'Unpin this post?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingPost?.action === 'hide'
                ? 'This removes the post from public feed visibility. The post is not deleted.'
                : 'This changes how the post is surfaced in its feed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={postAction.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={postAction.isPending}
              onClick={() => pendingPost && postAction.mutate(pendingPost)}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={sharePost !== null}
        onOpenChange={(open) =>
          !open && !moveAction.isPending && setSharePost(null)
        }
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share post to another feed</DialogTitle>
            <DialogDescription>
              Choose where this post should appear. The original post is
              updated; it is not duplicated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <select
              value={shareTarget}
              onChange={(event) =>
                setShareTarget(event.target.value as typeof shareTarget)
              }
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            >
              <option value="live">Live Feed</option>
              <option value="blocks">Blocks Feed</option>
              <option value="stocks">Stocks Feed</option>
            </select>
            {shareTarget === 'blocks' && (
              <select
                value={communityId}
                onChange={(event) => setCommunityId(event.target.value)}
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Select a Block</option>
                {toOptions(communities.data).map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name ||
                      option.title ||
                      option.slug ||
                      `Block ${option.id}`}
                  </option>
                ))}
              </select>
            )}
            {shareTarget === 'stocks' && (
              <select
                value={stockSymbol}
                onChange={(event) => setStockSymbol(event.target.value)}
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Select a stock</option>
                {(symbols.data ?? []).map((option) => (
                  <option key={option.symbol} value={option.symbol}>
                    {option.symbol}
                  </option>
                ))}
              </select>
            )}
            <select
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            >
              <option value="">Select a category (optional)</option>
              {toOptions(categories.data).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name ||
                    option.title ||
                    option.slug ||
                    `Category ${option.id}`}
                </option>
              ))}
            </select>
            <Button
              className="w-full"
              disabled={
                moveAction.isPending ||
                (shareTarget === 'blocks' && !communityId) ||
                (shareTarget === 'stocks' && !stockSymbol)
              }
              onClick={() => moveAction.mutate()}
            >
              <ArrowLeftRight className="size-4" />{' '}
              {moveAction.isPending ? 'Sharing…' : 'Share to feed'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={superPowersOpen} onOpenChange={setSuperPowersOpen}>
        <DialogContent className="w-[min(96vw,1500px)] max-w-[1500px]">
          <DialogHeader>
            <DialogTitle>Feed Super Powers</DialogTitle>
            <DialogDescription>
              Turn each feed and its sub-features on or off. These changes apply
              immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pb-2">
            {flags.isLoading ? (
              <LoadingState rows={6} />
            ) : (
              <FeedCard
                feed={activeFeed}
                flagMap={flagMap}
                onChange={change}
                wide
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
        <div className="flex items-center gap-1 rounded-full border bg-background/95 p-1.5 shadow-xl backdrop-blur">
          {(['live', 'blocks', 'stocks'] as FeedTab[]).map((feed) => (
            <Button
              key={feed}
              variant={selectedFeed === feed ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-full px-4"
              onClick={() => setSelectedFeed(feed)}
            >
              {feed === 'live'
                ? 'Live'
                : feed === 'blocks'
                  ? 'Blocks'
                  : 'Stocks'}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full px-4"
            onClick={() => setSuperPowersOpen(true)}
          >
            <Settings2 className="size-4" /> Super Powers
          </Button>
        </div>
      </div>
    </div>
  )
}

function toOptions(data: OptionsResponse | undefined): Option[] {
  return Array.isArray(data) ? data : (data?.results ?? [])
}

function FeedContentCard({
  post,
  onAction,
  onShare
}: {
  post: FeedPost
  onAction: (action: 'hide' | 'pin' | 'unpin') => void
  onShare: () => void
}) {
  const [mediaFailed, setMediaFailed] = useState(false)
  const author =
    post.author?.display_name || post.author?.username || 'XOBLACK member'
  const mediaItems = (post.media ?? []).filter((media) => media.url).slice(0, 4)

  return (
    <Card className="mb-5 break-inside-avoid overflow-hidden rounded-2xl border shadow-sm">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
            {author.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{author}</p>
            <p className="text-xs text-muted-foreground">
              @{post.author?.username || 'member'} ·{' '}
              {post.created_at
                ? new Date(post.created_at).toLocaleString()
                : 'Recently'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {post.is_pinned && (
            <Badge variant="default" className="gap-1">
              <Pin className="size-3" /> Pinned
            </Badge>
          )}
          <Badge variant="outline">
            {post.community_id
              ? 'Blocks'
              : post.stock_symbol
                ? `Stocks · ${post.stock_symbol}`
                : 'Live'}
          </Badge>
          <Button variant="ghost" size="icon-sm" aria-label="Post options">
            <MoreHorizontal className="size-4" />
          </Button>
        </div>
      </div>
      <div className="px-5 pb-4">
        <p className="whitespace-pre-wrap text-[15px] leading-6">
          {post.caption || ''}
        </p>
      </div>
      {mediaItems.length > 0 && !mediaFailed ? (
        <div
          className={cn(
            'grid gap-1 bg-muted',
            mediaItems.length > 1 && 'grid-cols-2'
          )}
        >
          {mediaItems.map((item, index) => {
            const url = new URL(item.url!, API_BASE_URL).toString()
            const video = item.media_type?.toLowerCase().includes('video')
            return video ? (
              <video
                key={`${url}-${index}`}
                src={url}
                controls
                onError={() => setMediaFailed(true)}
                className={cn(
                  'max-h-[620px] min-h-48 w-full bg-black object-contain',
                  mediaItems.length === 1 ? 'col-span-full' : 'aspect-square'
                )}
              />
            ) : (
              <img
                key={`${url}-${index}`}
                src={url}
                alt={item.alt_text || 'Feed post media'}
                onError={() => setMediaFailed(true)}
                className={cn(
                  'max-h-[620px] w-full bg-black object-contain',
                  mediaItems.length === 1 ? 'col-span-full' : 'aspect-square'
                )}
              />
            )
          })}
        </div>
      ) : mediaFailed ? (
        <div className="flex min-h-40 items-center justify-center bg-muted px-6 text-center text-sm text-muted-foreground">
          Media could not be loaded. The post text is still available below.
        </div>
      ) : !post.caption ? (
        <div className="flex min-h-40 items-center justify-center bg-gradient-to-br from-primary/15 via-muted to-muted">
          <FileText className="size-8 text-primary/60" />
        </div>
      ) : null}
      <div className="space-y-3 px-5 py-4">
        <div className="flex items-center justify-between border-b pb-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Heart className="size-3.5" /> {post.likes_count ?? 0}
          </span>
          <span className="inline-flex items-center gap-1">
            <MessageCircle className="size-3.5" /> {post.comments_count ?? 0}
          </span>
          <span className="inline-flex items-center gap-1">
            <Repeat2 className="size-3.5" /> {post.reposts_count ?? 0}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => onAction('hide')}>
            <Eye className="size-3.5" /> Hide
          </Button>
          <Button
            size="sm"
            variant={post.is_pinned ? 'secondary' : 'outline'}
            onClick={() => onAction(post.is_pinned ? 'unpin' : 'pin')}
          >
            <Pin className="size-3.5" /> {post.is_pinned ? 'Unpin' : 'Pin'}
          </Button>
          <Button size="sm" variant="outline" onClick={onShare}>
            <ArrowLeftRight className="size-3.5" /> Share to feed
          </Button>
        </div>
      </div>
    </Card>
  )
}

function FeedCard({
  feed,
  flagMap,
  onChange,
  wide = false
}: {
  feed: FeedConfig
  flagMap: Map<string, Flag>
  onChange: (flag: Flag, value: boolean) => void
  wide?: boolean
}) {
  const Icon = feed.icon
  const master = flagMap.get(feed.master)
  const visibleFeatures = feed.features.filter((feature) =>
    flagMap.has(feature.key)
  )
  const enabledCount = visibleFeatures.filter(
    (feature) => flagMap.get(feature.key)?.value
  ).length

  return (
    <Card className="overflow-hidden rounded-3xl">
      <div className="border-b bg-muted/30 p-5">
        <div className="flex items-start justify-between gap-3">
          <div
            className={cn(
              'flex size-11 items-center justify-center rounded-2xl bg-background shadow-sm',
              feed.color
            )}
          >
            <Icon className="size-5" />
          </div>
          {master && (
            <Switch
              checked={master.value}
              onCheckedChange={(value) => onChange(master, value)}
              aria-label={`Turn ${feed.title} ${master.value ? 'off' : 'on'}`}
            />
          )}
        </div>
        <div className="mt-5 flex items-center gap-2">
          <h2 className="text-lg font-semibold">{feed.title}</h2>
          <Badge variant={master?.value ? 'default' : 'secondary'}>
            {master?.value ? 'Live' : 'Off'}
          </Badge>
        </div>
        <p className="mt-1 text-sm font-medium text-muted-foreground">
          {feed.subtitle}
        </p>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {feed.description}
        </p>
        <p className="mt-4 text-xs font-medium text-muted-foreground">
          {enabledCount} of {visibleFeatures.length} features enabled
        </p>
      </div>
      <div
        className={cn(
          'divide-y',
          wide && 'grid gap-x-6 md:grid-cols-2 xl:grid-cols-3 xl:divide-y-0'
        )}
      >
        {visibleFeatures.map((feature) => {
          const flag = flagMap.get(feature.key)!
          return (
            <div
              key={feature.key}
              className="flex items-center justify-between gap-3 px-5 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{feature.label}</p>
                <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                  {feature.description}
                </p>
              </div>
              <Switch
                checked={flag.value}
                onCheckedChange={(value) => onChange(flag, value)}
                aria-label={`Turn ${feature.label} ${flag.value ? 'off' : 'on'}`}
              />
            </div>
          )
        })}
        {visibleFeatures.length === 0 && (
          <p className="px-5 py-4 text-sm text-muted-foreground">
            No controls are available for this feed.
          </p>
        )}
      </div>
    </Card>
  )
}
